import React, { useEffect, useRef, useState } from 'react';
import { Info, Copy, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

interface SuggestionInfoPopoverProps {
  className?: string;
}

const mediaLine = `"mediaLink": "https://www.youtube.com/watch?v=demoClip123",`;

const mediaSnippet = `{
  "title": "Interactive demo",
  "mediaLink": "https://www.youtube.com/watch?v=demoClip123",
  "description": "Walkthrough of the workspace UI"
}`;

const tagsExample = `[
  "image-generation",
  "marketing-assistant"
]`;

const promoLine = `"promo": "with PROMO!",`;

const promoSnippet = `"officialWebsite": {
  "title": "Official site",
  "url": "https://example.com/?ref=aizen",
  "promo": "with PROMO!"
}`;

const SuggestionInfoPopover = ({ className }: SuggestionInfoPopoverProps) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  // When rendered inside a modal dialog, portal the popover into that dialog so
  // it stays within the dialog's scroll-lock boundary and wheel scrolling works.
  const [container, setContainer] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (open) {
      setContainer((triggerRef.current?.closest('[role="dialog"]') as HTMLElement | null) ?? null);
    }
  }, [open]);

  const handleCopy = async (value: string, label: string) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        toast({ description: `${label} copied to clipboard.` });
      } else {
        throw new Error('Clipboard API unavailable');
      }
    } catch (error) {
      console.error('Failed to copy example snippet', error);
      toast({
        variant: 'destructive',
        description: 'Unable to copy automatically. Please select and copy the text manually.',
      });
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          type="button"
          size="icon"
          variant="ghost"
          aria-label="Show JSON editing guidelines"
          className={cn('text-muted-foreground hover:text-foreground', className)}
        >
          <Info className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        container={container}
        collisionPadding={16}
        className="relative w-[26rem] max-w-[calc(100vw-2rem)] space-y-4 overflow-y-auto text-sm max-h-[var(--radix-popover-content-available-height)]"
      >
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label="Close info"
          className="sticky top-0 z-20 ml-auto block rounded-sm bg-popover p-1 opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="space-y-2">
          <h4 className="text-base font-semibold">Tips</h4>
          <p className="text-xs text-muted-foreground">
            Use these snippets when editing raw JSON so media embeds and metadata follow the expected structure.
          </p>
        </div>

        <div className="rounded-md border bg-muted/40 p-3 space-y-2">
          <p className="text-xs font-medium uppercase text-muted-foreground">Media link snippet</p>
          <p className="text-xs text-muted-foreground">
            Place the line below directly under the capability or use case title. The generated description should remain after it.
          </p>
          <pre className="whitespace-pre-wrap rounded bg-background/70 p-2 text-xs font-mono">{mediaLine}</pre>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="w-full justify-center"
            onClick={() => handleCopy(mediaLine, 'mediaLink line')}
          >
            <Copy className="h-4 w-4" />
            Copy mediaLink line
          </Button>
          <p className="text-xs font-medium text-muted-foreground">Example:</p>
          <pre className="whitespace-pre-wrap rounded bg-background/70 p-2 text-xs font-mono">{mediaSnippet}</pre>
        </div>

        <div className="rounded-md border bg-muted/40 p-3 space-y-2">
          <p className="text-xs font-medium uppercase text-muted-foreground">Promo badge (referral links)</p>
          <p className="text-xs text-muted-foreground">
            Add <code className="font-mono">promo</code> inside <code className="font-mono">officialWebsite</code> to show a golden badge on the "Access it Here" button — handy for referral/affiliate URLs. Use <code className="font-mono">true</code> for the default "with PROMO!" text, or a string for custom text.
          </p>
          <pre className="whitespace-pre-wrap rounded bg-background/70 p-2 text-xs font-mono">{promoLine}</pre>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="w-full justify-center"
            onClick={() => handleCopy(promoLine, 'promo line')}
          >
            <Copy className="h-4 w-4" />
            Copy promo line
          </Button>
          <p className="text-xs font-medium text-muted-foreground">Example:</p>
          <pre className="whitespace-pre-wrap rounded bg-background/70 p-2 text-xs font-mono">{promoSnippet}</pre>
        </div>

        <div className="rounded-md border bg-muted/40 p-3 space-y-2">
          <p className="text-xs font-medium uppercase text-muted-foreground">Tag reminder</p>
          <p className="text-xs text-muted-foreground">
            Reuse existing tags. Submit a new tag only when the current catalog cannot describe the capability.
          </p>
          <pre className="whitespace-pre-wrap rounded bg-background/70 p-2 text-xs font-mono">{tagsExample}</pre>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="w-full justify-center"
            onClick={() => handleCopy(tagsExample, 'tag array example')}
          >
            <Copy className="h-4 w-4" />
            Copy tag array example
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default SuggestionInfoPopover;






